import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import RightDrawer from '../../../../shared/components/form/RightDrawer';
import { assignmentService } from '../../../../services/assignmentService';
import projectService from '../../../../services/projectService';
import { equipmentService } from '../../../../services/equipmentService';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Users,
  User,
  Search,
  Plus,
  Trash2,
  UserCheck,
  Shield,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Edit3,
  Check
} from 'lucide-react';

// Modern Clean Brand Colors
const BRAND_COLORS = {
  primary: "#339274",
  primaryHover: "#29795f",
  primaryLight: "#F8FCFA",
  primarySoft: "#EDF7F2",
  accent: "#A3CD39",
  dark: "#007F66",
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444",
  errorLight: "#FEF2F2",
  warning: "#F59E0B",
  warningLight: "#FFFBEB",
  info: "#3B82F6",
  infoLight: "#EFF6FF",
};

interface Assignee {
  _id: string;
  name: string;
  email: string;
  mobile?: string;
  role: {
    _id: string;
    name: string;
  };
  assignedProjects?: Array<{
    projectId: string;
    projectName: string;
    equipmentCount: number;
  }>;
  totalProjectsAssigned?: number;
  totalEquipmentAssigned?: number;
  lastAssignmentUpdate?: string;
  approvalStatus?: 'pending' | 'approved' | 'rejected' | 'modified';
}

interface ManageProjectResourcesProps {
  isOpen?: boolean;
  onClose?: () => void;
  projectId?: string;
}

function ManageProjectResources({ isOpen: propIsOpen, onClose, projectId: propProjectId }: ManageProjectResourcesProps) {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const projectId = propProjectId || urlProjectId || '';
  const isOpen = propIsOpen !== undefined ? propIsOpen : true;

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  // State
  const [loading, setLoading] = useState(false);
  const [project, setProject] = useState<any>(null);
  const [assignedResources, setAssignedResources] = useState<Assignee[]>([]);
  const [availableResources, setAvailableResources] = useState<Assignee[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'Site Engineer' | 'Site Supervisor'>('all');
  const [selectedResourceIds, setSelectedResourceIds] = useState<string[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState<'assigned' | 'available'>('assigned');

  // Enhanced state for warnings and equipment tracking
  const [showRemoveWarning, setShowRemoveWarning] = useState(false);
  const [resourceToRemove, setResourceToRemove] = useState<any>(null);
  const [resourceEquipmentData, setResourceEquipmentData] = useState<Record<string, any>>({});
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);

  // Load initial data when drawer opens
  useEffect(() => {
    if (isOpen && projectId) {
      loadProject();
      loadProjectResources();
      loadAvailableResources();
    }
  }, [isOpen, projectId]);

  // Refresh available resources when assigned resources change
  useEffect(() => {
    if (assignedResources.length > 0) {
      loadAvailableResources();
    }
  }, [assignedResources]);

  const loadProject = async () => {
    try {
      setLoading(true);
      const response = await projectService.getProjectById(projectId!);
      setProject(response?.data || response);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const loadProjectResources = async () => {
    try {
      // Get resources assigned to this project
      const queryParams = { projectId };
      const response = await assignmentService.getEngineersWithAssignments(queryParams);
      const engineers = response?.engineers || [];
      setAssignedResources(engineers);

      // Load equipment data for assigned resources
      if (engineers.length > 0) {
        const resourceIds = engineers.map(e => e._id);
        await loadResourceEquipmentData(resourceIds);
      }
    } catch (error: any) {
      console.error('Failed to load project resources:', error);
      setAssignedResources([]);
    }
  };

  const loadAvailableResources = async () => {
    try {
      // Get all available resources (not filtered by project)
      const response = await assignmentService.getEngineersWithAssignments({});
      const allResources = response?.engineers || [];

      // Get currently assigned resource IDs from both the project and assignedResources state
      const projectAssignedIds = [
        ...(project?.assignedResources?.siteEngineers || []),
        ...(project?.assignedResources?.siteSupervisors || [])
      ].map(id => id.toString());

      const stateAssignedIds = assignedResources.map(r => r._id.toString());

      // Combine both sources to ensure we don't show any assigned resources
      const allAssignedIds = [...new Set([...projectAssignedIds, ...stateAssignedIds])];

      const unassignedResources = allResources.filter(resource =>
        !allAssignedIds.includes(resource._id.toString())
      );

      console.log('Available resources filtered:', {
        total: allResources.length,
        assigned: allAssignedIds.length,
        available: unassignedResources.length
      });

      setAvailableResources(unassignedResources);
    } catch (error: any) {
      console.error('Failed to load available resources:', error);
      setAvailableResources([]);
    }
  };

  // Filter resources based on search and role
  const filteredAssignedResources = useMemo(() => {
    return assignedResources.filter(resource => {
      const matchesSearch = resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = roleFilter === 'all' || resource.role.name === roleFilter;
      return matchesSearch && matchesRole;
    });
  }, [assignedResources, searchTerm, roleFilter]);

  const filteredAvailableResources = useMemo(() => {
    return availableResources.filter(resource => {
      const matchesSearch = resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = roleFilter === 'all' || resource.role.name === roleFilter;
      return matchesSearch && matchesRole;
    });
  }, [availableResources, searchTerm, roleFilter]);

  // Load equipment data for resources to check assignments
  const loadResourceEquipmentData = async (resourceIds: string[]) => {
    try {
      const equipmentData: Record<string, any> = {};

      for (const resourceId of resourceIds) {
        try {
          // Get equipment assigned to this resource in this project
          const response = await equipmentService.getByProject(projectId!);
          const projectEquipment = Array.isArray(response) ? response : ((response as any)?.data || []);

          const assignedEquipment = projectEquipment.filter((eq: any) =>
            eq.assignedTo &&
            (eq.assignedTo._id === resourceId || eq.assignedTo.id === resourceId)
          );

          equipmentData[resourceId] = {
            equipmentCount: assignedEquipment.length,
            equipment: assignedEquipment,
            hasAssignments: assignedEquipment.length > 0
          };
        } catch (error) {
          console.error(`Failed to load equipment for resource ${resourceId}:`, error);
          equipmentData[resourceId] = { equipmentCount: 0, equipment: [], hasAssignments: false };
        }
      }

      setResourceEquipmentData(prev => ({ ...prev, ...equipmentData }));
      return equipmentData;
    } catch (error) {
      console.error('Failed to load resource equipment data:', error);
      return {};
    }
  };

  // Handle adding resources to project
  const handleAddResources = async () => {
    if (selectedResourceIds.length === 0) {
      toast.error('Please select at least one resource to add');
      return;
    }

    setIsUpdating(true);
    try {
      const selectedResources = filteredAvailableResources.filter(resource =>
        selectedResourceIds.includes(resource._id)
      );

      const siteEngineers = selectedResources
        .filter(r => r.role.name === 'Site Engineer')
        .map(r => r._id);

      const siteSupervisors = selectedResources
        .filter(r => r.role.name === 'Site Supervisor')
        .map(r => r._id);

      // Debug logging
      console.log('🔍 Frontend: Adding resources:', {
        projectId,
        selectedResourceIds,
        siteEngineers,
        siteSupervisors,
        selectedResources: selectedResources.map(r => ({ id: r._id, name: r.name, role: r.role.name }))
      });

      // Use dedicated resource management endpoint
      await projectService.addProjectResources(projectId!, {
        siteEngineers,
        siteSupervisors
      });

      const resourceNames = selectedResources.map(r => r.name).join(', ');
      toast.success(`Successfully added ${selectedResources.length} resource(s): ${resourceNames}`);

      // Reset selection and reload data
      setSelectedResourceIds([]);
      await Promise.all([
        loadProject(),
        loadProjectResources(),
        loadAvailableResources()
      ]);

    } catch (error: any) {
      console.error('Failed to add resources:', error);
      toast.error(error.message || 'Failed to add resources to project');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle removing resources from project with warning
  const handleRemoveResources = async () => {
    if (selectedResourceIds.length === 0) {
      toast.error('Please select at least one resource to remove');
      return;
    }

    // Check for equipment assignments before removing
    const equipmentData = await loadResourceEquipmentData(selectedResourceIds);
    const resourcesWithEquipment = selectedResourceIds.filter(id =>
      equipmentData[id]?.hasAssignments
    );

    if (resourcesWithEquipment.length > 0) {
      const selectedResources = filteredAssignedResources.filter(resource =>
        selectedResourceIds.includes(resource._id)
      );
      setResourceToRemove(selectedResources);
      setShowRemoveWarning(true);
      return;
    }

    // If no equipment assignments, proceed directly
    await performResourceRemoval();
  };

  // Perform the actual resource removal
  const performResourceRemoval = async () => {
    setIsUpdating(true);
    try {
      const selectedResources = filteredAssignedResources.filter(resource =>
        selectedResourceIds.includes(resource._id)
      );

      // Remove equipment assignments first
      for (const resourceId of selectedResourceIds) {
        const equipmentData = resourceEquipmentData[resourceId];
        if (equipmentData?.hasAssignments) {
          try {
            // Here you would call an API to unassign equipment from the resource
            // For now, we'll log it - you'll need to implement the actual API call
            console.log(`Removing ${equipmentData.equipmentCount} equipment assignments from resource ${resourceId}`);
            // await equipmentService.unassignFromResource(resourceId, projectId!);
          } catch (error) {
            console.error(`Failed to remove equipment assignments for resource ${resourceId}:`, error);
          }
        }
      }

      // Debug logging
      console.log('🔍 Frontend: Removing resources:', {
        projectId,
        selectedResourceIds,
        selectedResources: selectedResources.map(r => ({ id: r._id, name: r.name, role: r.role.name }))
      });

      // Use dedicated resource management endpoint
      await projectService.removeProjectResources(projectId!, selectedResourceIds);

      const resourceNames = selectedResources.map(r => r.name).join(', ');
      const equipmentCount = selectedResourceIds.reduce((sum, id) =>
        sum + (resourceEquipmentData[id]?.equipmentCount || 0), 0
      );

      if (equipmentCount > 0) {
        toast.success(`Successfully removed ${selectedResources.length} resource(s) and ${equipmentCount} equipment assignments: ${resourceNames}`);
      } else {
        toast.success(`Successfully removed ${selectedResources.length} resource(s): ${resourceNames}`);
      }

      // Reset selection and reload data
      setSelectedResourceIds([]);
      setShowRemoveWarning(false);
      setResourceToRemove(null);
      await Promise.all([
        loadProject(),
        loadProjectResources(),
        loadAvailableResources()
      ]);

    } catch (error: any) {
      console.error('Failed to remove resources:', error);
      toast.error(error.message || 'Failed to remove resources from project');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle role change (reassign between Site Engineer and Site Supervisor)
  const handleChangeRole = async (resourceId: string, newRole: 'Site Engineer' | 'Site Supervisor') => {
    setIsUpdating(true);
    try {
      const resource = assignedResources.find(r => r._id === resourceId);

      // Debug logging
      console.log('🔍 Frontend: Changing resource role:', {
        projectId,
        resourceId,
        resourceName: resource?.name,
        currentRole: resource?.role.name,
        newRole
      });

      // Use dedicated resource management endpoint
      await projectService.changeResourceRole(projectId!, resourceId, newRole);

      toast.success(`Successfully changed ${resource?.name} to ${newRole}`);

      // Reload data
      await Promise.all([
        loadProject(),
        loadProjectResources(),
        loadAvailableResources()
      ]);

    } catch (error: any) {
      console.error('Failed to change resource role:', error);
      toast.error(error.message || 'Failed to change resource role');
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading || !project) {
    return (
      <RightDrawer
        open={isOpen}
        onClose={handleClose}
        title="Manage Project Resources"
        size="60"
      >
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-gray-500" />
            <p className="text-gray-600">Loading project...</p>
          </div>
        </div>
      </RightDrawer>
    );
  }

  const currentResources = activeTab === 'assigned' ? filteredAssignedResources : filteredAvailableResources;
  const showAddButton = activeTab === 'available' && selectedResourceIds.length > 0;
  const showRemoveButton = activeTab === 'assigned' && selectedResourceIds.length > 0;

  return (
    <RightDrawer
      open={isOpen}
      onClose={handleClose}
      title="Manage Project Resources"
      size="60"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
        {/* Header */}
        <div className="flex items-center justify-between -mt-2">
          <div>
            <p className="text-gray-600 text-sm">{project.projectName} ({project.projectId})</p>
          </div>

          <button
            onClick={() => {
              loadProject();
              loadProjectResources();
              loadAvailableResources();
            }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <User size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Site Engineers</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {project.assignedResources?.siteEngineers?.length || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Shield size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Site Supervisors</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {project.assignedResources?.siteSupervisors?.length || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Users size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Resources</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {(project.assignedResources?.siteEngineers?.length || 0) +
                    (project.assignedResources?.siteSupervisors?.length || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-300 overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => {
                  setActiveTab('assigned');
                  setSelectedResourceIds([]);
                }}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${activeTab === 'assigned'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <UserCheck size={16} />
                  Assigned Resources ({assignedResources.length})
                </div>
              </button>
              <button
                onClick={() => {
                  setActiveTab('available');
                  setSelectedResourceIds([]);
                }}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${activeTab === 'available'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Plus size={16} />
                  Available to Add ({availableResources.length})
                </div>
              </button>
            </div>
          </div>

          {/* Controls */}
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                {/* Search */}
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search resources..."
                    className="w-64 pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Role Filter */}
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value as any)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Roles</option>
                  <option value="Site Engineer">Site Engineer</option>
                  <option value="Site Supervisor">Site Supervisor</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                {selectedResourceIds.length > 0 && (
                  <span className="text-sm text-gray-600">
                    {selectedResourceIds.length} selected
                  </span>
                )}

                {showAddButton && (
                  <button
                    onClick={handleAddResources}
                    disabled={isUpdating}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 hover:shadow-md disabled:opacity-50"
                    style={{ backgroundColor: BRAND_COLORS.success }}
                  >
                    {isUpdating ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Plus size={16} />
                    )}
                    Add to Project
                  </button>
                )}

                {showRemoveButton && (
                  <button
                    onClick={handleRemoveResources}
                    disabled={isUpdating}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-all duration-200 hover:shadow-md disabled:opacity-50"
                  >
                    {isUpdating ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Trash2 size={16} />
                    )}
                    Remove from Project
                  </button>
                )}
              </div>
            </div>

            {/* Selection Summary */}
            {selectedResourceIds.length > 0 && (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <span className="text-sm font-medium text-blue-800">
                  {selectedResourceIds.length} resource(s) selected
                </span>
                <button
                  onClick={() => setSelectedResourceIds([])}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Clear selection
                </button>
              </div>
            )}
          </div>

          {/* Modern Compact Resources Grid */}
          <div className="p-6">
            {currentResources.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                  <Users size={32} className="text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {searchTerm || roleFilter !== 'all'
                    ? 'No matching resources found'
                    : activeTab === 'assigned'
                      ? 'No resources assigned yet'
                      : 'No available resources'
                  }
                </h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  {searchTerm || roleFilter !== 'all'
                    ? 'Try adjusting your search or filter criteria to find resources'
                    : activeTab === 'assigned'
                      ? 'Add resources from the "Available to Add" tab to get started'
                      : 'All available resources are already assigned to this project'
                  }
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {currentResources.map((resource) => {
                  const equipmentData = resourceEquipmentData[resource._id];
                  const isSelected = selectedResourceIds.includes(resource._id);
                  const isEditingRole = editingRoleId === resource._id;

                  return (
                    <motion.div
                      key={resource._id}
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      whileHover={{ y: -2 }}
                      className={`relative p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer group ${resource.approvalStatus === 'rejected'
                        ? 'border-red-500 bg-red-50 shadow-md ring-2 ring-red-300'
                        : resource.approvalStatus === 'modified'
                          ? 'border-amber-500 bg-amber-50 shadow-md ring-2 ring-amber-300'
                          : resource.approvalStatus === 'approved'
                            ? 'border-green-400 bg-green-50'
                            : isSelected
                              ? 'border-blue-400 bg-blue-50 shadow-md'
                              : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                        }`}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedResourceIds(prev => prev.filter(id => id !== resource._id));
                        } else {
                          setSelectedResourceIds(prev => [...prev, resource._id]);
                        }
                      }}
                    >
                      {/* Selection Indicator */}
                      <div className="absolute top-3 right-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isSelected
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300 group-hover:border-gray-400'
                          }`}>
                          {isSelected && <Check size={12} className="text-white" />}
                        </div>
                      </div>

                      {/* Resource Avatar */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-sm ${resource.role.name === 'Site Engineer'
                          ? 'bg-gradient-to-br from-blue-400 to-blue-600 text-white'
                          : 'bg-gradient-to-br from-purple-400 to-purple-600 text-white'
                          }`}>
                          {resource.role.name === 'Site Engineer' ? (
                            <User size={18} />
                          ) : (
                            <Shield size={18} />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 truncate text-sm">
                            {resource.name}
                          </h4>
                          <p className="text-xs text-gray-500 truncate">
                            {resource.email}
                          </p>
                          {resource.mobile && (
                            <p className="text-xs text-gray-600 truncate">
                              📱 {resource.mobile}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Role Badge with Edit */}
                      <div className="flex items-center justify-between mb-3">
                        {isEditingRole && activeTab === 'assigned' ? (
                          <div className="flex items-center gap-2 flex-1">
                            <select
                              value={resource.role.name}
                              onChange={(e) => {
                                handleChangeRole(resource._id, e.target.value as any);
                                setEditingRoleId(null);
                              }}
                              onBlur={() => setEditingRoleId(null)}
                              autoFocus
                              className="flex-1 px-2 py-1 text-xs border border-blue-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="Site Engineer">Site Engineer</option>
                              <option value="Site Supervisor">Site Supervisor</option>
                            </select>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${resource.role.name === 'Site Engineer'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700'
                              }`}>
                              {resource.role.name === 'Site Engineer' ? 'Engineer' : 'Supervisor'}
                            </span>

                            {/* Approval Status Badge */}
                            {resource.approvalStatus === 'rejected' && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-[10px] font-bold text-center">
                                ✗ REJECTED
                              </span>
                            )}
                            {resource.approvalStatus === 'modified' && (
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-bold text-center" title={`Modified resource: ${resource.name}`}>
                                Modified ({resource.name})
                              </span>
                            )}
                            {resource.approvalStatus === 'approved' && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-[10px] font-bold text-center">
                                ✓ Approved
                              </span>
                            )}
                            {resource.approvalStatus === 'pending' && (
                              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-[10px] font-bold text-center">
                                ⏳ Pending
                              </span>
                            )}
                          </div>
                        )}

                        {/* Edit Role Button (only for assigned resources) */}
                        {activeTab === 'assigned' && !isEditingRole && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingRoleId(resource._id);
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                            title="Change role"
                          >
                            <Edit3 size={12} />
                          </button>
                        )}
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div className="text-center p-2 bg-gray-50 rounded-lg">
                          <div className="text-sm font-bold text-blue-600">
                            {resource.totalProjectsAssigned || 0}
                          </div>
                          <div className="text-xs text-gray-500">Projects</div>
                        </div>
                        <div className="text-center p-2 bg-gray-50 rounded-lg">
                          <div className="text-sm font-bold text-green-600">
                            {equipmentData?.equipmentCount || resource.totalEquipmentAssigned || 0}
                          </div>
                          <div className="text-xs text-gray-500">Equipment</div>
                        </div>
                      </div>

                      {/* Equipment Warning Indicator */}
                      {activeTab === 'assigned' && equipmentData?.hasAssignments && (
                        <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                          <AlertTriangle size={12} className="text-amber-600" />
                          <span className="text-xs text-amber-700 font-medium">
                            Has {equipmentData.equipmentCount} equipment assigned
                          </span>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Remove Warning Modal */}
        <AnimatePresence>
          {showRemoveWarning && resourceToRemove && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-[10000] flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              >
                {/* Warning Header */}
                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-orange-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <AlertTriangle size={20} className="text-amber-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Remove Resources Warning</h3>
                      <p className="text-sm text-gray-600">
                        Some resources have assigned equipment that will be removed
                      </p>
                    </div>
                  </div>
                </div>

                {/* Warning Content */}
                <div className="p-6">
                  <div className="mb-6">
                    <p className="text-gray-700 mb-4">
                      The following resources have equipment and tasks assigned to them.
                      Removing these resources will also unassign their equipment:
                    </p>

                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {resourceToRemove.map((resource: any) => {
                        const equipmentData = resourceEquipmentData[resource._id];
                        if (!equipmentData?.hasAssignments) return null;

                        return (
                          <div key={resource._id} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-center gap-3 mb-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${resource.role.name === 'Site Engineer'
                                ? 'bg-blue-100 text-blue-600'
                                : 'bg-purple-100 text-purple-600'
                                }`}>
                                {resource.role.name === 'Site Engineer' ? (
                                  <User size={14} />
                                ) : (
                                  <Shield size={14} />
                                )}
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">{resource.name}</div>
                                <div className="text-sm text-gray-600">{resource.role.name}</div>
                              </div>
                            </div>

                            <div className="ml-11">
                              <div className="text-sm text-amber-700 font-medium">
                                Will remove {equipmentData.equipmentCount} equipment assignment(s)
                              </div>
                              {equipmentData.equipment && equipmentData.equipment.length > 0 && (
                                <div className="mt-2 text-xs text-gray-600">
                                  Equipment: {equipmentData.equipment.slice(0, 3).map((eq: any) => eq.equipmentName || eq.name).join(', ')}
                                  {equipmentData.equipment.length > 3 && ` and ${equipmentData.equipment.length - 3} more...`}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={16} className="text-red-600 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-red-800">
                          This action cannot be undone
                        </div>
                        <div className="text-sm text-red-700 mt-1">
                          Equipment assignments will be permanently removed and will need to be reassigned manually.
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowRemoveWarning(false);
                        setResourceToRemove(null);
                        setSelectedResourceIds([]);
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={performResourceRemoval}
                      disabled={isUpdating}
                      className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUpdating ? (
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 size={16} className="animate-spin" />
                          Removing...
                        </div>
                      ) : (
                        'Remove Resources & Equipment'
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </motion.div>
    </RightDrawer>
  );
}

export default ManageProjectResources;
